import type { IBulkRegisterProgress } from '@hit-game/shared/types';
import { useEffect, useRef } from 'react';

import { useServerEvent } from './useServerEvent';

/** Gom ping dồn trong cửa sổ ngắn thành MỘT lần refresh (một lô nổ nhiều ping gần nhau). */
const COALESCE_MS = 200;

/**
 * Nghe SSE `event: bulk-register-progress` cho một lô, gom cụm rồi gọi `onProgress`.
 *
 * Ping CHỈ mang đếm (done/total) — không credential. Caller dùng nó như tín hiệu
 * "có tiến triển, đi đọc status" chứ không phải nguồn dữ liệu dòng.
 *
 * @param batchId lô đang theo dõi; `null` thì không nghe.
 */
export function useBulkRegisterProgress(
  batchId: string | null,
  onProgress: (progress: IBulkRegisterProgress) => void,
  enabled = true,
): void {
  const cbRef = useRef(onProgress);
  cbRef.current = onProgress;

  const pendingRef = useRef<IBulkRegisterProgress | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useServerEvent(
    'bulk-register-progress',
    (data) => {
      let progress: IBulkRegisterProgress;
      try {
        progress = JSON.parse(data) as IBulkRegisterProgress;
      } catch {
        return; // Bỏ frame hỏng định dạng.
      }
      if (!batchId || progress.batchId !== batchId) return; // Ping của lô khác.
      pendingRef.current = progress;
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const next = pendingRef.current;
        pendingRef.current = null;
        if (next) cbRef.current(next);
      }, COALESCE_MS);
    },
    enabled && !!batchId,
  );

  // Hủy timer gom cụm khi đổi lô / tắt nghe / unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = null;
    };
  }, [batchId, enabled]);
}
